import React, { useEffect, useRef, type JSX } from 'react';

import ReactECharts from 'echarts-for-react';
import { WidgetHeader, WidgetFooter } from 'pages/WidgetManagement/common';
import { Divider, Paper, Stack } from '@mui/material';
import WidgetNotConfigured from 'pages/EditWidget/WIdgetNotConfigured';
import { CustomTransComponent } from 'components';
import TelemetryChart from './TelemetryChart';
import { HeaderAction } from '../aggregated_trends/HeaderAction';
import PieChart from './PieChart';
import { useTypedSelector } from '@fiji/common';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';
import { useRBAC } from 'hooks';

type TelemetryChartProps = {
    type?: 'bar' | 'column';
    widgetTypeId?: string;
    route?: string;
    state?: string;
    maxCount?: number;
    secondary: any[];
    widgetName?: string;
    [key: string]: any;
};

// Preview/configured containers (e.g. the widget bar) don't always give the widget a definite
// height to inherit, which makes the `100%`/`calc(100% - 97px)` heights below collapse and, in
// turn, breaks the getBoundingClientRect()-based sizing used for the bar/column/pie charts.
// Falling back to a fixed height in those modes keeps the preview chart usable.
const PREVIEW_CHART_HEIGHT = 480;
const isPreviewLikeMode = (mode?: string): boolean => mode === 'preview' || mode === 'configured';

const Component = ({
    widgetData,
    mode,
    color,
    widgetFilter,
    downloadCSVHandler,
}: {
    widgetData: TelemetryChartProps;
    mode: string;
    color: string;
    widgetFilter?: any;
    downloadCSVHandler?: any;
}): JSX.Element => {
    const chartWidget = JSON.parse(JSON.stringify(widgetData));
    if (chartWidget.widgetTypeId !== 'charts')
        if (chartWidget?.isDataSortDesc) {
            chartWidget?.secondary?.sort((a: { value: number }, b: { value: number }) => a?.value - b?.value);
        } else {
            chartWidget?.secondary?.reverse();
        }
    const componentRef = React.useRef<HTMLDivElement | null>(null);
    const [chartRect, setChartRect] = React.useState<any>(null);
    const [chartHeight, setChartHeight] = React.useState<any>(null);
    const rolePermissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(rolePermissions);
    const canViewDetailChart = hasPermission('telemetry-charts_view-devices');
    const canViewDetailChartExpired = hasPermission('telemetry-charts_view-devices_hasExpired');
    const chartRef: any = useRef(null);
    const [option, setOption] = React.useState<any>({
        tooltip: { trigger: 'item' },
        legend: { show: false },
        grid: { containLabel: true },
        xAxis: {},
        yAxis: {},
        series: [],
    });

    const handleResizeComponent = (): void => {
        const chartContainer = componentRef.current?.getElementsByClassName('padding-0')?.[0]?.getBoundingClientRect();
        setChartHeight(Math.floor(((chartContainer?.height ?? 0) / 100) * 99));
        const teleChart = componentRef.current?.getElementsByClassName('teleChart')?.[0]?.getBoundingClientRect();
        setChartRect({
            labelWidth: chartWidget?.type === 'bar' ? chartContainer?.width : chartContainer?.height,
            chartHeight: teleChart?.height,
        });
    };

    React.useEffect(() => {
        handleResizeComponent();
        window?.addEventListener('resize', handleResizeComponent);
        // Widgets can be resized by dragging a grid/splitter handle without firing a window
        // "resize" event, so a ResizeObserver is needed to keep chartRect/chartHeight in sync
        // with the actual rendered container size (otherwise axes don't rescale and can overlap).
        const el = componentRef.current;
        const observer = new ResizeObserver(handleResizeComponent);
        if (el) observer.observe(el);
        return () => {
            observer.disconnect();
            window?.removeEventListener('resize', handleResizeComponent);
        };
    }, []);

    const getSeriesData = (): any[] =>
        chartWidget?.secondary?.slice(0, chartWidget?.maxCount ?? chartWidget?.secondary?.length).map((item: any) => ({
            name: item?.displayName ?? '',
            type: 'bar' as const,
            data: [{ value: item?.value ?? 0, unit: item?.unit ? ` ${item?.unit}` : '%' }],
            itemStyle: {
                color: color,
                borderRadius: chartWidget?.type === 'bar' ? [0, 10, 10, 0] : [10, 10, 0, 0],
            },
            barWidth: 30,
            label: {
                show: true,
                position: chartWidget?.type === 'bar' ? 'right' : 'top',
                rotate: chartWidget?.type === 'bar' ? 0 : -90,
                formatter: (params: any): string => {
                    const unitSuffix = params.data?.unit ?? '%';
                    return `{val|${params.value}} {unit|${unitSuffix}} {nm|${params.seriesName}}`;
                },
                rich: {
                    val: { fontSize: 14, fontWeight: 300 },
                    unit: { fontSize: 14, fontWeight: 200 },
                    nm: { fontSize: 12, fontWeight: 300, padding: [0, 0, 0, 16] },
                },
            },
        }));

    const getIntervalValue = (dataValue: any): any => {
        const value: number = dataValue?.reduce?.(
            (max: any, current: any) => (current.value > max.value ? current : max),
            dataValue?.[0]
        )?.value;
        if (value > 10) return Math.ceil(value / 10);
        return value ? (value / 12)?.toFixed?.(2) : undefined;
    };

    useEffect(() => {
        const isBar = chartWidget?.type === 'bar';
        const data: any = JSON.parse(JSON.stringify(chartWidget?.secondary ?? []));
        const unit =
            data?.reduce?.((max: any, current: any) => (current.value > max.value ? current : max), data[0])?.unit ??
            '';
        const tickInterval = getIntervalValue(data);

        const valueAxis: any = {
            type: 'value',
            axisLabel: { formatter: `{value} ${unit}` },
            splitLine: { show: false },
            axisLine: { show: true, lineStyle: { color: isBar ? '#f0ebeb' : '#ffffff', width: 2 } },
            axisTick: { show: false },
            ...(tickInterval !== null ? { interval: Number(tickInterval) } : {}),
        };

        const categoryAxis: any = {
            type: 'category',
            data: [''],
            axisLabel: { show: false },
            splitLine: { show: false },
            axisLine: { show: false },
            axisTick: { show: false },
        };

        setOption({
            tooltip: {
                trigger: 'item',
                formatter: (params: any) => {
                    const unitSuffix = params.data?.unit ?? '';
                    return `${params.seriesName}: ${params.value}${unitSuffix}`;
                },
            },
            legend: { show: false },
            grid: { containLabel: true, left: 10, right: 10, top: 10, bottom: 10 },
            xAxis: isBar ? valueAxis : categoryAxis,
            yAxis: isBar ? categoryAxis : valueAxis,
            series: getSeriesData() ?? [],
        });
    }, [widgetData?.secondary, chartHeight, chartRect]);

    const getAbsentDataTitle = (): string | undefined => {
        if (chartWidget?.multiDevice && chartWidget?.assets?.length === 0) {
            return 'Widget Not Configured';
        }
        if ((!chartWidget?.secondary?.length && !chartWidget?.multiDevice) || chartWidget?.assets?.length) {
            return 'No Data Found';
        }
        return undefined;
    };

    const getChart = (): JSX.Element => {
        if (chartWidget.widgetTypeId !== 'consumption_breakdown') {
            if (chartWidget?.type === 'pie') {
                return (
                    <Stack
                        sx={{
                            height: `calc(100% - 97px)`,
                            overflow: 'auto',
                        }}
                        className="teleChart"
                    >
                        <PieChart chartWidget={chartWidget} chartRect={chartRect} />
                    </Stack>
                );
            }
            return <TelemetryChart widgetData={chartWidget} mode={mode} chartRect={chartRect} />;
        }
        return (
            <Stack className="padding-0" sx={{ height: 'calc(100% - 97px)' }}>
                {chartWidget?.secondary?.length ? (
                    <ReactECharts
                        option={option}
                        ref={chartRef}
                        style={{ height: chartHeight ? `${chartHeight}px` : '100%', width: '100%' }}
                        notMerge={true}
                    />
                ) : (
                    <WidgetNotConfigured title={getAbsentDataTitle()} />
                )}
            </Stack>
        );
    };

    return (
        <Paper
            sx={{ height: isPreviewLikeMode(mode) ? PREVIEW_CHART_HEIGHT : '100%', overflow: 'auto' }}
            ref={componentRef}
        >
            {(chartWidget.widgetTypeId !== 'consumption_breakdown' || chartWidget.viewTitleBar) &&
                mode !== 'viewAll' && (
                    <WidgetHeader
                        mode={mode}
                        widgetData={chartWidget}
                        widgetFilter={widgetFilter}
                        {...(chartWidget?.viewTimePeriod ||
                            (chartWidget?.widgetTypeId === 'aggregated_trends' && {
                                headerAction: (
                                    <HeaderAction
                                        mode={mode}
                                        widgetData={chartWidget}
                                        widgetFilter={widgetFilter}
                                        downloadCSVHandler={downloadCSVHandler}
                                    />
                                ),
                            }))}
                    />
                )}
            {mode !== 'edit' && <Divider />}
            {getChart()}
            {mode !== 'viewAll' && (canViewDetailChart || canViewDetailChartExpired) && (
                <WidgetFooter
                    mode={mode}
                    widgetData={{ ...chartWidget, viewAllButton: true }}
                    ids={[]}
                    {...(chartWidget.widgetTypeId !== 'consumption_breakdown' && {
                        actionBtnName: <CustomTransComponent translationKey={'COMMON:VIEW_FULL_CHART'} />,
                    })}
                />
            )}
        </Paper>
    );
};
export default Component;
